import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { getDatabase, ref, get } from 'firebase/database';
import { Building, BuildingModel, BuildingSummary } from "../types/protocols";

interface BuildingContextType {
    currentBuilding: Building | null;
    loading: boolean;
    error: string | null;
    fetchBuilding: (buildingKey: string) => Promise<void>;
    clearBuilding: () => void;
}

const BuildingContext = createContext<BuildingContextType>({
    currentBuilding: null,
    loading: false,
    error: null,
    fetchBuilding: async () => {},
    clearBuilding: () => {},
});

interface BuildingProviderProps {
    children: ReactNode;
}

export const BuildingProvider: React.FC<BuildingProviderProps> = ({ children }) => {
    const [currentBuilding, setCurrentBuilding] = useState<Building | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const fetchBuilding = useCallback(async (buildingKey: string) => {
        if (!buildingKey) {
            setError('Building key is required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const db = getDatabase();
            const modelRef = ref(db, `buildings/${buildingKey}`);
            const modelSnapshot = await get(modelRef);

            const summaryRef = ref(db, `buildingsSummary/${buildingKey}`);
            const summarySnapshot = await get(summaryRef);

            if (modelSnapshot.exists() && summarySnapshot.exists()) {
                const model = modelSnapshot.val() as BuildingModel;
                const summary = summarySnapshot.val() as BuildingSummary;

                const building: Building = {
                    key: buildingKey,
                    model,
                    summary
                };

                setCurrentBuilding(building);
            } else {
                setError('Building not found');
            }
        } catch (err) {
            console.error('Error fetching building:', err);
            setError('Failed to load building data');
        } finally {
            setLoading(false);
        }
    }, []);

    const clearBuilding = useCallback(() => {
        setCurrentBuilding(null);
        setError(null);
    }, []);

    const value = {
        currentBuilding,
        loading,
        error,
        fetchBuilding,
        clearBuilding
    };

    return (
        <BuildingContext.Provider value={value}>
            {children}
        </BuildingContext.Provider>
    );
};

export const useBuilding = () => {
    const context = useContext(BuildingContext);

    if (!context) {
        throw new Error('useBuilding must be used within a BuildingProvider');
    }

    return context;
};